import { useTransform, type MotionValue, type Variants } from "motion/react";

export const EASE = [0.22, 1, 0.36, 1] as const;

export const viewportOnce = { once: true, margin: "0px 0px -12% 0px" } as const;

export const SCROLL_OFFSET = -96;

/** One scroll page inside a sticky multi-step section. */
export const VIEWPORT_PAGE_VH = 85;

/** Extra scroll height below single-screen sections before the next section enters. */
export const SECTION_DWELL_VH = 50;

/** Fraction of each step's scroll segment where content stays fixed (0–1). */
export const STEP_DWELL_RATIO = 0.72;

/** Remap linear scroll progress into step plateaus with brief transitions. */
export function remapProgressWithDwell(
  progress: number,
  stepCount: number,
  dwellRatio = STEP_DWELL_RATIO,
): number {
  if (stepCount <= 1) return progress;

  const segment = 1 / stepCount;
  const dwell = segment * dwellRatio;
  const transition = segment - dwell;
  const segmentIndex = Math.min(stepCount - 1, Math.floor(progress / segment));
  const local = progress - segmentIndex * segment;

  if (local <= dwell) {
    return segmentIndex / (stepCount - 1);
  }

  const t = Math.min(1, (local - dwell) / transition);
  return Math.min(1, (segmentIndex + t) / (stepCount - 1));
}

/** Scroll progress with dwell plateaus for multi-step sticky sections. */
export function useDwellProgress(
  scrollYProgress: MotionValue<number>,
  stepCount: number,
) {
  return useTransform(scrollYProgress, (p) =>
    remapProgressWithDwell(p, stepCount),
  );
}

/** Opacity for step `index` of `total` steps, crossfading at boundaries. */
export function useStepOpacity(
  progress: MotionValue<number>,
  index: number,
  total: number,
) {
  const stepSize = 1 / total;
  const center = (index + 0.5) * stepSize;
  const fade = stepSize * 0.35;

  return useTransform(progress, (p) => {
    const dist = Math.abs(p - center);
    return dist < fade ? 1 - dist / fade : 0;
  });
}

/** Active step index for scroll progress in a multi-step section. */
export function getActiveStepIndex(progress: number, total: number): number {
  return Math.min(total - 1, Math.max(0, Math.round(progress * (total - 1))));
}

/** Opacity 1 for the active step, 0 for all others — no crossfade at boundaries. */
export function useStepCutOpacity(
  progress: MotionValue<number>,
  index: number,
  total: number,
) {
  return useTransform(progress, (p) => {
    return getActiveStepIndex(p, total) === index ? 1 : 0;
  });
}

/** Opacity for one step from raw scroll progress — dwell at 1, crossfade only in segment tail. */
export function stepSegmentTransitionOpacity(
  progress: number,
  index: number,
  total: number,
  dwellRatio = STEP_DWELL_RATIO,
): number {
  if (total <= 1) return index === 0 ? 1 : 0;

  const segment = 1 / total;
  const dwell = segment * dwellRatio;
  const transition = segment - dwell;

  // Outgoing: fade out at end of this step's segment
  if (index < total - 1) {
    const segStart = index * segment;
    const transStart = segStart + dwell;
    const transEnd = segStart + segment;
    if (progress >= transStart && progress < transEnd) {
      const t = Math.min(1, (progress - transStart) / transition);
      return 1 - t;
    }
  }

  // Incoming: fade in during previous segment's transition tail
  if (index > 0) {
    const prevSegStart = (index - 1) * segment;
    const transStart = prevSegStart + dwell;
    const transEnd = prevSegStart + segment;
    if (progress >= transStart && progress < transEnd) {
      const t = Math.min(1, (progress - transStart) / transition);
      return t;
    }
  }

  // Dwell: full opacity while this step is held
  const segStart = index * segment;
  const dwellEnd = segStart + dwell;
  if (progress >= segStart && progress < dwellEnd) {
    return 1;
  }

  // Last step: stay visible through end of section
  if (index === total - 1 && progress >= segStart) {
    return 1;
  }

  return 0;
}

/** Active step from raw scroll — switches to incoming step once crossfade passes 50%. */
export function getActiveStepIndexFromScroll(
  progress: number,
  total: number,
  dwellRatio = STEP_DWELL_RATIO,
): number {
  if (total <= 1) return 0;

  const segment = 1 / total;
  const dwell = segment * dwellRatio;
  const transition = segment - dwell;
  const segmentIndex = Math.min(total - 1, Math.floor(progress / segment));
  const local = progress - segmentIndex * segment;

  if (local > dwell && segmentIndex < total - 1) {
    const t = Math.min(1, (local - dwell) / transition);
    return t >= 0.5 ? segmentIndex + 1 : segmentIndex;
  }

  return segmentIndex;
}

/** Scroll-linked opacity: dwell at 1, crossfade only before the next step opens. */
export function useStepSegmentTransitionOpacity(
  scrollYProgress: MotionValue<number>,
  index: number,
  total: number,
  dwellRatio = STEP_DWELL_RATIO,
) {
  return useTransform(scrollYProgress, (p) =>
    stepSegmentTransitionOpacity(p, index, total, dwellRatio),
  );
}

/** Like useStepOpacity but inactive steps stay visible at `floor` (default 20%). */
export function useStepPresenceOpacity(
  progress: MotionValue<number>,
  index: number,
  total: number,
  floor = 0.2,
) {
  const peak = useStepOpacity(progress, index, total);
  return useTransform(peak, (v) => floor + (1 - floor) * v);
}

/** Fade + rise, with an optional stagger index passed via `custom`. */
export const fadeUp: Variants = {
  hidden: { opacity: 0, y: 26 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.85, delay: i * 0.08, ease: EASE },
  }),
};

/** Accent rule draw — scaleX 0→1 from the left. */
export const ruleDraw: Variants = {
  hidden: { scaleX: 0 },
  show: {
    scaleX: 1,
    transition: { duration: 0.8, ease: EASE },
  },
};

/** Tighter fade + rise for editorial labels and body copy. */
export const fadeUpBody: Variants = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.85, ease: EASE },
  },
};

/** Editorial headline reveal — larger rise, longer duration. */
export const fadeUpEditorial: Variants = {
  hidden: { opacity: 0, y: 40 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 1, delay: 0.2, ease: EASE },
  },
};

/** Editorial subtext — enters after headline. */
export const fadeUpSubtext: Variants = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.85, delay: 0.3, ease: EASE },
  },
};

/** Eyebrow label — enters after accent rule. */
export const fadeUpLabel: Variants = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.85, delay: 0.1, ease: EASE },
  },
};

/** Orchestrates header entrance with 0.1s stagger between direct children. */
export const editorialSequence: Variants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0,
    },
  },
};

/** Pillar card grid — staggered children after a 0.4s lead-in. */
export const pillarStagger: Variants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.4,
    },
  },
};
